import React, { Fragment, FunctionComponent, useEffect } from "react";
import { PAGE_TYPE } from "./page.types";

import { CLOSE_ALL_OVERLAYS } from "layout/redux/overlay.actions";
import { Helmet } from "react-helmet-async";
import { thunkUpdatePage } from "app/umbraco/redux/page.actions";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import ContentPage from "pages/ContentPage/ContentPage";
import ErrorPage from "pages/ErrorPage/ErrorPage";
import EventPage from "pages/EventPage/EventPage";
import getPageMeta from "app/umbraco/helpers/getPageMeta";
import HomePage from "pages/HomePage/HomePage";
import isBrowserContext from "helpers/isBrowserContext";
import NewsPage from "pages/NewsPage/NewsPage";
import NotFoundPage from "pages/NotFoundPage/NotFoundPage";
import OverviewPage from "pages/OverviewPage/OverviewPage";
import SearchPage from "pages/SearchPage/SearchPage";

const UmbracoPage: FunctionComponent = () => {
  const match = useRouteMatch();
  const page = useSelector((state) => state.page.content);
  const settings = useSelector((state) => state.settings.content);

  // TODO: Declare type for UmbracoPage thunk actions
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<any>();

  useEffect(() => {
    if (match.url === "/preview") return;
    dispatch(thunkUpdatePage(match.url, history));
  }, [dispatch, match.url, history]);

  useEffect(() => {
    if (match.url !== "/preview") return;
    const query = new URLSearchParams(location.search);
    const previewId = query.get("id");
    const culture = query.get("culture");
    if (previewId) {
      dispatch(thunkUpdatePage(`/${previewId}.aspx`, history));
    }
  }, [dispatch, match.url, location.search, history]);  

  // Page update - callback
  useEffect(() => {
    if (page?._url) {
      // #SCROLL_RESTORATION: Resetting scrollbar on page changes to top page page
      if (isBrowserContext()) {
        window.scrollTo({
          left: 0,
          top: location.state?.scrollY ?? 0,
          behavior: "instant",
        } as any); // behavior instant is working, types is not.
      }

      // #OVERLAY: Reset the redux drawer state to None when changing to a new page
      if (page?._url) {
        dispatch({
          type: CLOSE_ALL_OVERLAYS,
        });
      }
    }
  }, [page]);

  const renderHead = (page: PAGE_TYPE): JSX.Element => {
    // TODO: Open Graph
    const {
      language,
      metaTitle,
      metaDescription,
      metaKeywords,
      metaCanonical,
      robots,
      socialTitle,
      socialDescription,
      socialImage,
      url,
    } = getPageMeta(page, settings);

    return (
      <Helmet>
        <html lang={language} />
        <title>{metaTitle}</title>

        <meta name="title" content={metaTitle} />
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <meta name="canonical" content={metaCanonical} />
        <meta name="robots" content={robots} />

        <meta property="og:title" content={socialTitle} />
        <meta property="og:description" content={socialDescription} />
        <meta property="og:image" content={socialImage} />
        <meta property="og:locale" content={language} />
        <meta property="og:site_name" content="WLS" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
      </Helmet>
    );
  };

  const renderBody = (page: PAGE_TYPE): JSX.Element => {
    // TODO: Schema JSON-LD
    // TODO: Schema Breadcrumb

    switch (page?._contentType) {
      case "homePage": {
        return <HomePage {...page} />;
      }
      case "contentPage": {
        return <ContentPage {...page} />;
      }
      case "eventPage": {
        return <EventPage {...page} />;
      }
      case "newsPage": {
        return <NewsPage {...page} />;
      }
      case "overviewPage": {
        return <OverviewPage {...page} />;
      }
      case "searchPage": {
        return <SearchPage {...page} />;
      }
      case "notFoundPage": {
        return <NotFoundPage {...page} />;
      }
      case "errorPage": {
        return <ErrorPage />;
      }
      default: {
        return <Fragment></Fragment>;
      }
    }
  };

  return (
    <Fragment>
      {renderHead(page)}
      {renderBody(page)}
    </Fragment>
  );
};

export default UmbracoPage;
